import { api } from './api'
import type { DonationLookup } from './allodons'

/**
 * LOT-NUMÉRO-INCONNU — les deux lectures du panneau « Numéro inconnu » : ce
 * que le CRM sait d'un numéro SANS fiche contact, pour ce tenant.
 *
 *   - `history`   → `GET /calls/by-phone/:phone/history` (journals, hors gel) :
 *                   tous les appels du tenant rapprochés du numéro (fiche,
 *                   patte sortante, patte entrante ; formes d'outre-mer
 *                   comprises, cf. `phoneMatchForms`), `startedAt` desc, 50 au
 *                   plus — l'endpoint ne pagine pas ;
 *   - `donations` → `GET /allodons/donations/by-phone/:phone`, aperçu borné.
 *
 * Les deux partent en REQUÊTE DE FOND (`X-Background-Request`) : sans cet
 * en-tête, l'intercepteur axios toaste toute erreur non-401 — or un 404
 * AlloDons (tenant sans raccordement) doit simplement MASQUER la section, et
 * un échec d'historique s'affiche dans le panneau lui-même.
 *
 * Normalisation au bord (registre #193) : une réponse hors contrat (corps
 * vide, page HTML d'un proxy, frontend déployé avant son backend) rend une
 * liste vide / `null`, jamais un plantage de l'écran.
 */

const BACKGROUND = { 'X-Background-Request': 'true' } as const

/** Nombre de dons montrés dans l'aperçu du panneau. */
export const DONATIONS_PREVIEW_COUNT = 5

export interface PhoneHistoryCall {
  id: string
  direction: 'INBOUND' | 'OUTBOUND' | null
  /** `CallStatus` brut (schema.prisma) — traduit par la surface. */
  status: string
  /** `CallOutcome` brut (schema.prisma) — traduit par la surface. */
  outcome: string | null
  duration: number
  startedAt: string | null
  createdAt: string
  notes: string | null
  user: { id: string; name: string | null; email: string } | null
  campaign: { id: string; name: string } | null
  /** Relation `recording` (0..1) réduite à sa présence. */
  hasRecording: boolean
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const stringOrNull = (value: unknown): string | null =>
  typeof value === 'string' ? value : null

function normalizeCall(raw: unknown): PhoneHistoryCall | null {
  if (!isRecord(raw) || typeof raw.id !== 'string') return null
  const user = isRecord(raw.user) && typeof raw.user.id === 'string'
    ? {
        id: raw.user.id,
        name: stringOrNull(raw.user.name),
        email: typeof raw.user.email === 'string' ? raw.user.email : '',
      }
    : null
  const campaign =
    isRecord(raw.campaign) && typeof raw.campaign.id === 'string'
      ? { id: raw.campaign.id, name: typeof raw.campaign.name === 'string' ? raw.campaign.name : '' }
      : null
  const direction =
    raw.direction === 'INBOUND' || raw.direction === 'OUTBOUND' ? raw.direction : null
  return {
    id: raw.id,
    direction,
    status: typeof raw.status === 'string' ? raw.status : '',
    outcome: stringOrNull(raw.outcome),
    duration: typeof raw.duration === 'number' && Number.isFinite(raw.duration) ? raw.duration : 0,
    startedAt: stringOrNull(raw.startedAt),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    notes: stringOrNull(raw.notes),
    user,
    campaign,
    hasRecording: isRecord(raw.recording),
  }
}

/** Hors contrat ⇒ `[]` ; une ligne sans identifiant est écartée. */
export function normalizePhoneHistory(data: unknown): PhoneHistoryCall[] {
  if (!Array.isArray(data)) return []
  return data.map(normalizeCall).filter((call): call is PhoneHistoryCall => call !== null)
}

function normalizeDonations(data: unknown): DonationLookup | null {
  if (!isRecord(data) || !isRecord(data.summary) || !Array.isArray(data.items)) return null
  return data as unknown as DonationLookup
}

export const unknownNumberApi = {
  /** Historique des appels du tenant pour ce numéro (toutes ses formes). */
  history: async (phone: string): Promise<PhoneHistoryCall[]> => {
    const { data } = await api.get(`/calls/by-phone/${encodeURIComponent(phone)}/history`, {
      headers: BACKGROUND,
    })
    return normalizePhoneHistory(data)
  },

  /**
   * Dons AlloDons du numéro — `null` dès que la lecture échoue (404 : pas de
   * raccordement ; réseau ; réponse hors contrat) : la section se masque.
   */
  donations: async (phone: string): Promise<DonationLookup | null> => {
    try {
      const { data } = await api.get(`/allodons/donations/by-phone/${encodeURIComponent(phone)}`, {
        params: { perPage: DONATIONS_PREVIEW_COUNT },
        headers: BACKGROUND,
      })
      return normalizeDonations(data)
    } catch {
      return null
    }
  },
}

export default unknownNumberApi
